import React, { useState } from "react";
import { CalendarClock, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { SlotPicker } from "@user/components/SlotPicker";

const BLOCKED = {
  too_close: "Rescheduling closes 2 hours before the consultation",
  already_rescheduled: "Already rescheduled once",
  instant: "Instant consultations can't be rescheduled",
  started: "Consultation already started",
};

// One-time, client-side move of a paid scheduled booking to another free slot (same lawyer, no payment change).
export function RescheduleButton({ consult, onRescheduled, size = "sm" }) {
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState("");
  const [slot, setSlot] = useState("");
  const [busy, setBusy] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  if (consult.can_reschedule === undefined) return null;
  const reason = consult.reschedule_blocked_reason;
  if (!consult.can_reschedule && !(reason in BLOCKED)) return null;

  const confirm = async () => {
    if (!slot) return;
    setBusy(true);
    try {
      const r = await api.post(`/consultations/${consult.id}/reschedule`, { slot_date: date, slot_time: slot });
      toast.success(`Rescheduled to ${r.data.slot_date} · ${r.data.slot_time} IST — your lawyer has been notified`);
      setOpen(false);
      onRescheduled?.(r.data);
    } catch (e) {
      const status = e.response?.status;
      toast.error(e.response?.data?.detail || "Could not reschedule");
      if (status === 409) { setSlot(""); setRefreshKey((n) => n + 1); }
    } finally { setBusy(false); }
  };

  return (
    <>
      <Button variant="outline" disabled={!consult.can_reschedule} onClick={() => setOpen(true)} title={!consult.can_reschedule ? BLOCKED[reason] : undefined}
        data-testid={`reschedule-${consult.id}`} data-blocked={reason || ""}
        className={`rounded-full font-semibold text-xs ${size === "sm" ? "h-9 px-4" : "h-10 px-5"} border-[#0A2540]/20 disabled:opacity-60`}>
        <CalendarClock size={13} /> {consult.can_reschedule ? "Reschedule" : BLOCKED[reason]}
      </Button>
      <Dialog open={open} onOpenChange={(o) => { if (!busy) setOpen(o); }}>
        <DialogContent className="w-[calc(100vw-2rem)] sm:max-w-lg max-h-[90vh] overflow-y-auto overflow-x-hidden" data-testid="reschedule-dialog">
          <DialogHeader>
            <DialogTitle>Reschedule consultation</DialogTitle>
            <DialogDescription>
              Currently <strong>{consult.slot_date} · {consult.slot_time} IST</strong> with {consult.lawyer_name}. You can move this booking <strong>once</strong>; the price and consultation type stay the same and your lawyer is notified immediately.
            </DialogDescription>
          </DialogHeader>
          <div className="min-w-0 w-full">
            <SlotPicker lawyerId={consult.lawyer_id} duration={consult.duration_minutes || 15} date={date} slot={slot} refreshKey={refreshKey}
              onChange={(d, s) => { setDate(d); setSlot(s); }} />
          </div>
          <div className="flex justify-end gap-2 mt-2">
            <Button variant="ghost" onClick={() => setOpen(false)} disabled={busy} data-testid="reschedule-cancel">Keep current time</Button>
            <Button onClick={confirm} disabled={!slot || busy} data-testid="reschedule-confirm" className="adv-btn-gold font-bold">
              {busy ? <Loader2 size={14} className="animate-spin" /> : slot ? `Move to ${slot} IST` : "Pick a slot"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
